import { runWithCartLock } from "../concurrency/cartLock";
import { getCartStoreConfiguration, reportCartDiagnostic } from "../config/cartRuntimeConfig";
import {
    collectCartStorageDiagnostic,
    readCartSnapshot,
    writeCartSnapshot,
} from "../storage/cartStorage";
import {
    CartPersistenceError,
    type CartCommitResult,
    type CartItem,
    type CartOperation,
    type CartPersistedState,
} from "../types/cart.types";
import { applyCartOperations } from "./cartOperations";

const areItemsEqual = (left: CartItem[], right: CartItem[]): boolean =>
    left === right || JSON.stringify(left) === JSON.stringify(right);

const describeOperations = (operations: CartOperation[]): Record<string, unknown>[] =>
    operations.map((operation) => {
        switch (operation.type) {
            case "add":
                return {
                    type: operation.type,
                    idProduct: operation.item.idProduct,
                    idProductVariant: operation.item.idProductVariant,
                    quantity: operation.item.quantity,
                    price: operation.item.price,
                    stock: operation.item.stock,
                    attributeCount: operation.item.attributes?.length ?? 0,
                };
            case "remove":
                return {
                    type: operation.type,
                    idProduct: operation.idProduct,
                    idProductVariant: operation.idProductVariant,
                };
            case "updateQuantity":
                return {
                    type: operation.type,
                    idProduct: operation.idProduct,
                    idProductVariant: operation.idProductVariant,
                    quantity: operation.quantity,
                };
            case "clear":
                return { type: operation.type };
        }
    });

const persistOperations = (
    baseSnapshot: CartPersistedState,
    operations: CartOperation[],
): CartCommitResult => {
    const nextItems = applyCartOperations(baseSnapshot.items, operations);
    if (areItemsEqual(baseSnapshot.items, nextItems)) {
        return {
            items: baseSnapshot.items,
            revision: baseSnapshot.revision,
            changed: false,
        };
    }

    const nextSnapshot: CartPersistedState = {
        items: nextItems,
        revision: baseSnapshot.revision + 1,
        updatedAt: new Date().toISOString(),
    };
    writeCartSnapshot(nextSnapshot);

    return {
        items: nextItems,
        revision: nextSnapshot.revision,
        changed: true,
    };
};

export const commitCartOperations = async (
    operations: CartOperation[],
    legacyBaseItems: CartItem[] = [],
): Promise<CartCommitResult> => {
    try {
        if (getCartStoreConfiguration().concurrencyMode === "locked") {
            return await runWithCartLock(() =>
                persistOperations(readCartSnapshot({ persistRecovery: true }), operations),
            );
        }

        const persistedSnapshot = readCartSnapshot();
        return persistOperations({ ...persistedSnapshot, items: legacyBaseItems }, operations);
    } catch (error) {
        reportCartDiagnostic({
            kind: "MUTATION_FAILED",
            timestampUtc: new Date().toISOString(),
            error,
            context: {
                concurrencyMode: getCartStoreConfiguration().concurrencyMode,
                operations: describeOperations(operations),
                legacyBaseItemCount: legacyBaseItems.length,
                storageAtFailure: collectCartStorageDiagnostic(),
            },
        });
        throw error;
    }
};

export const validateAndRepairCartPersistence = async (): Promise<CartPersistedState> => {
    try {
        if (getCartStoreConfiguration().concurrencyMode === "locked") {
            return await runWithCartLock(() => readCartSnapshot({ persistRecovery: true }));
        }

        return readCartSnapshot({ persistRecovery: true });
    } catch (error) {
        reportCartDiagnostic({
            kind: "VALIDATION_FAILED",
            timestampUtc: new Date().toISOString(),
            error,
            context: {
                concurrencyMode: getCartStoreConfiguration().concurrencyMode,
                trigger: "cart_opened",
                storageAtFailure: collectCartStorageDiagnostic(),
            },
        });
        throw error;
    }
};

export const replaceLegacyCartItems = async (items: CartItem[]): Promise<CartCommitResult> => {
    try {
        if (getCartStoreConfiguration().concurrencyMode === "locked") {
            throw new CartPersistenceError(
                "UNSAFE_REPLACE_DISABLED",
                "No se puede reemplazar persistentemente todo el carrito en modo locked.",
            );
        }

        const persistedSnapshot = readCartSnapshot();
        if (areItemsEqual(persistedSnapshot.items, items)) {
            return {
                items: persistedSnapshot.items,
                revision: persistedSnapshot.revision,
                changed: false,
            };
        }

        const nextSnapshot: CartPersistedState = {
            items,
            revision: persistedSnapshot.revision + 1,
            updatedAt: new Date().toISOString(),
        };
        writeCartSnapshot(nextSnapshot);

        return { items, revision: nextSnapshot.revision, changed: true };
    } catch (error) {
        reportCartDiagnostic({
            kind: "LEGACY_REPLACE_FAILED",
            timestampUtc: new Date().toISOString(),
            error,
            context: {
                concurrencyMode: getCartStoreConfiguration().concurrencyMode,
                replacementItemCount: items.length,
                storageAtFailure: collectCartStorageDiagnostic(),
            },
        });
        throw error;
    }
};
